import React, { useEffect, useState } from "react";
import { Plus, Play, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TASK_TYPES = [
    "document_expiry_reminder",
    "payroll_reminder",
    "gov_reminder",
    "report_generation",
    "payslip_email",
    "escalation",
];
const FREQUENCIES = ["once", "daily", "weekly", "monthly"];
const nice = (s) => String(s || "—").replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function Automation() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const defaultNext = () => {
        const d = new Date(Date.now() + 60 * 60 * 1000); // 1h from now
        return d.toISOString().slice(0, 16);
    };
    const [form, setForm] = useState({ task_type: "document_expiry_reminder", frequency: "daily", name: "", next_run_at: defaultNext(), within_days: 30 });

    const load = async () => {
        try {
            const { data } = await api.get("/automation/tasks");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load tasks");
        }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            const payload = {
                task_type: form.task_type,
                frequency: form.frequency,
                name: form.name || nice(form.task_type),
                next_run_at: new Date(form.next_run_at).toISOString(),
                config: form.task_type === "document_expiry_reminder" ? { within_days: Number(form.within_days) || 30 } : {},
            };
            await api.post("/automation/tasks", payload);
            toast.success("Scheduled task created");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const toggle = async (t) => {
        try {
            await api.put(`/automation/tasks/${t.id}`, { enabled: !t.enabled });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete scheduled task "${t.name}"?`)) return;
        try {
            await api.delete(`/automation/tasks/${t.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const runDue = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/automation/run-due");
            toast.success(`Executed ${data.executed} due task(s)`);
            load();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Run failed");
        } finally { setRunning(false); }
    };

    return (
        <div>
            <PageHeader
                title="Automation & Scheduler"
                subtitle="Recurring reminders and reports. Due tasks run via the jobs runner (cron) or the Run Due button."
                actions={
                    <div className="flex items-center gap-2">
                        <button onClick={runDue} disabled={running} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-2" data-testid="run-due-btn"><Play className="size-4" /> Run Due Now</button>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-task-btn"><Plus className="size-4" /> New Task</button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Scheduled Task</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label>Task Type</Label>
                                        <select className="w-full h-10 px-3 rounded-md border border-border" value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })} data-testid="task-type">
                                            {TASK_TYPES.map((t) => <option key={t} value={t}>{nice(t)}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={nice(form.task_type)} /></div>
                                    <div>
                                        <Label>Frequency</Label>
                                        <select className="w-full h-10 px-3 rounded-md border border-border" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="task-frequency">
                                            {FREQUENCIES.map((f) => <option key={f} value={f}>{nice(f)}</option>)}
                                        </select>
                                    </div>
                                    <div><Label>First Run</Label><Input type="datetime-local" value={form.next_run_at} onChange={(e) => setForm({ ...form, next_run_at: e.target.value })} data-testid="task-next-run" /></div>
                                    {form.task_type === "document_expiry_reminder" && (
                                        <div className="col-span-2"><Label>Expiring within (days)</Label><Input type="number" min={1} value={form.within_days} onChange={(e) => setForm({ ...form, within_days: e.target.value })} /></div>
                                    )}
                                </div>
                                <DialogFooter>
                                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="task-create-submit">Create Task</button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />
            <TableWrap>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Frequency</th>
                        <th>Next Run</th>
                        <th>Last Run</th>
                        <th className="num">Runs</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((t) => (
                        <tr key={t.id} data-testid={`task-row-${t.id}`}>
                            <td className="font-medium">{t.name}</td>
                            <td>{nice(t.task_type)}</td>
                            <td className="capitalize">{t.frequency}</td>
                            <td className="font-mono text-xs">{t.next_run_at ? formatDateTime(t.next_run_at) : "—"}</td>
                            <td className="font-mono text-xs">{t.last_run_at ? formatDateTime(t.last_run_at) : "—"}</td>
                            <td className="num">{t.run_count || 0}</td>
                            <td><StatusBadge value={t.enabled ? "active" : "disabled"} /></td>
                            <td>
                                <div className="flex items-center gap-2">
                                    <button className="text-xs text-primary" onClick={() => toggle(t)}>{t.enabled ? "Disable" : "Enable"}</button>
                                    <button className="text-destructive" onClick={() => remove(t)} title="Delete"><Trash2 className="size-4" /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={8}><Empty title="No scheduled tasks" subtitle="Create a recurring reminder or report." /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
